import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getPostpartumCareCompletionRateDetail,
  getNursingCareCompletionRateDetail,
  getHousekeepingCompletionRateDetail,
  getCateringCompletionRateDetail,
  getHomeServiceCompletionRateDetail,
} from '@/service/completionRate';

// ==================== 初始状态 ====================

const defaultState = {
  fetching: false,
  error: null,
  query: {},
  // 不同服务类型的数据存储
  data: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
  },
  // 按 serviceType 存储数据
  maternityCare: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {},
  },
  nursing: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {},
  },
  housekeeping: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {},
  },
  catering: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {},
  },
  homeBabyCare: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {},
  },
};

// ==================== 服务类型映射配置 ====================

/**
 * 服务类型映射函数 - 根据serviceType调用不同的API服务
 * 实现灵活的多服务支持，便于扩展新的API接口
 * @param {string} serviceType - 服务类型标识
 * @param {Object} query - 查询参数
 * @returns {Function} 返回对应的API调用函数
 */
const switchPayload = (serviceType, query) => {
  const payload = {
    maternityCare: () =>
      getPostpartumCareCompletionRateDetail(query), // 产康完成率明细
    nursing: () => getNursingCareCompletionRateDetail(query), // 护理部完成率明细
    housekeeping: () =>
      getHousekeepingCompletionRateDetail(query), // 客房完成率明细
    catering: () => getCateringCompletionRateDetail(query), // 餐饮完成率明细
    homeBabyCare: () =>
      getHomeServiceCompletionRateDetail(query), // 到家完成率明细
    undefined: () =>
      console.error(
        'Redux: fetchCompletionRateProfile 请求未定义 serviceType 类型'
      ),
  };
  return payload[serviceType] || payload.undefined;
};

// ==================== 异步Action ====================

/**
 * 异步获取完成率明细的thunk action
 * 支持多服务类型
 * @param {Object} options - 请求选项
 * @param {string} options.serviceType - 服务类型，必填 (maternityCare, nursing, housekeeping, catering, homeBabyCare)
 * @param {Object} options.query - 查询参数对象
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchCompletionRateProfile = createAsyncThunk(
  'completionRateProfile/fetchCompletionRateProfile',
  async ({ serviceType, query = {} }, { rejectWithValue }) => {
    try {
      if (!serviceType) {
        return rejectWithValue('serviceType 参数是必需的');
      }
      const response = await switchPayload(serviceType, query)();
      return {
        data: response.data,
        serviceType,
        query,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || `获取${serviceType}完成率明细失败`
      );
    }
  }
);

// ==================== Redux Slice ====================

const completionRateProfileSlice = createSlice({
  name: 'completionRateProfile',
  initialState: defaultState,
  reducers: {
    resetCompletionRateProfileState: (state, action) => {
      const serviceType = action.payload?.serviceType;
      if (serviceType && state[serviceType]) {
        state[serviceType] = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20,
          query: {},
        };
      } else {
        // 重置所有状态
        state.query = {};
        state.data = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20,
        };
        Object.keys(defaultState).forEach((key) => {
          if (
            key !== 'fetching' &&
            key !== 'error' &&
            key !== 'query' &&
            key !== 'data'
          ) {
            state[key] = {
              list: [],
              total: 0,
              pageNumber: 1,
              pageSize: 20,
              query: {},
            };
          }
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompletionRateProfile.pending, (state, action) => {
        state.fetching = true;
        state.error = null;
        state.query = action.meta.arg.query;
      })
      .addCase(fetchCompletionRateProfile.fulfilled, (state, action) => {
        state.fetching = false;
        state.error = null;
        const { data, serviceType, query } = action.payload;

        // 更新通用 data
        state.data =
          data || {
            list: [],
            total: 0,
            pageNumber: 1,
            pageSize: 20,
          };
        state.query = query;

        // 按 serviceType 存储数据
        if (serviceType && state[serviceType]) {
          state[serviceType] = {
            ...(data || {
              list: [],
              total: 0,
              pageNumber: 1,
              pageSize: 20,
            }),
            query: query,
          };
        }
      })
      .addCase(fetchCompletionRateProfile.rejected, (state, action) => {
        state.fetching = false;
        state.error = action.payload;
        state.data = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20,
        };
      });
  },
});

export const { resetCompletionRateProfileState } =
  completionRateProfileSlice.actions;
export default completionRateProfileSlice.reducer;
